import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface ReportPropertyModalProps {
  propertyId: string;
  propertyTitle: string;
  landlordName: string;
  reporterUserId?: string;
  reporterEmail?: string;
  onClose: () => void;
}

const REASONS = [
  { value: 'fraud', label: '詐騙嫌疑', icon: 'ri-spy-line', desc: '要求預先匯款或其他可疑行為' },
  { value: 'fake_info', label: '不實資訊', icon: 'ri-file-damage-line', desc: '房源照片或描述嚴重不符' },
  { value: 'illegal', label: '違規內容', icon: 'ri-forbid-line', desc: '違反平台規定或法律' },
  { value: 'duplicate', label: '重複刊登', icon: 'ri-file-copy-line', desc: '相同房源重複出現' },
  { value: 'other', label: '其他問題', icon: 'ri-more-line', desc: '其他需要管理員處理的問題' },
];

export default function ReportPropertyModal({
  propertyId,
  propertyTitle,
  landlordName,
  reporterUserId,
  reporterEmail,
  onClose,
}: ReportPropertyModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    setSubmitting(true);
    try {
      await supabase.from('property_reports').insert({
        property_id: propertyId,
        property_title: propertyTitle,
        landlord_name: landlordName,
        reporter_user_id: reporterUserId || null,
        reporter_email: reporterEmail || null,
        reason: selectedReason,
        details: details.trim() || null,
        status: 'pending',
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-50">
              <i className="ri-flag-2-line text-rose-500 text-base"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-800">舉報此房源</h3>
              <p className="text-xs text-stone-400 truncate max-w-[200px]">{propertyTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-400 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {submitted ? (
          /* Success State */
          <div className="px-6 py-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-50 mx-auto mb-4">
              <i className="ri-check-line text-teal-500 text-2xl"></i>
            </div>
            <h4 className="text-stone-800 font-bold text-base mb-2">舉報已送出</h4>
            <p className="text-stone-500 text-sm leading-relaxed mb-6">
              我們已收到您的舉報，管理員將盡快審閱，感謝您協助維護平台安全！
            </p>
            <button
              onClick={onClose}
              className="px-8 py-2.5 bg-teal-500 text-white text-sm rounded-full hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              關閉
            </button>
          </div>
        ) : (
          /* Form */
          <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <p className="text-xs text-stone-500 leading-relaxed bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
              <i className="ri-information-line text-amber-500 mr-1"></i>
              請選擇舉報原因，管理員將在審閱後採取適當行動。濫用舉報功能可能影響您的帳號。
            </p>

            {/* Reason Selection */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-stone-600">舉報原因 <span className="text-rose-400">*</span></p>
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setSelectedReason(r.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer ${
                    selectedReason === r.value
                      ? 'border-rose-300 bg-rose-50'
                      : 'border-stone-100 bg-stone-50 hover:border-stone-200'
                  }`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${
                    selectedReason === r.value ? 'bg-rose-100' : 'bg-white border border-stone-200'
                  }`}>
                    <i className={`${r.icon} text-sm ${selectedReason === r.value ? 'text-rose-500' : 'text-stone-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${selectedReason === r.value ? 'text-rose-700' : 'text-stone-700'}`}>
                      {r.label}
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">{r.desc}</p>
                  </div>
                  {selectedReason === r.value && (
                    <i className="ri-check-line text-rose-500 flex-shrink-0"></i>
                  )}
                </button>
              ))}
            </div>

            {/* Details */}
            <div>
              <p className="text-xs font-semibold text-stone-600 mb-1.5">補充說明（選填）</p>
              <textarea
                value={details}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setDetails(e.target.value);
                }}
                placeholder="請具體描述遇到的問題，例如：對方要求匯款到不明帳戶..."
                className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-xl resize-none focus:outline-none focus:border-rose-300 text-stone-700 placeholder-stone-300"
                rows={3}
              />
              <p className="text-xs text-stone-300 text-right mt-0.5">{details.length} / 500</p>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!selectedReason || submitting}
              className="w-full py-3 bg-rose-500 text-white text-sm font-medium rounded-xl hover:bg-rose-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin"></i>送出中...</>
              ) : (
                <><i className="ri-flag-2-line"></i>送出舉報</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
